import { css } from 'aphrodite';
import React from 'react';
import { IFormField } from '../../../../../../../extViewmodels';
import DisclosureDownIconUrl from '../../../../../../../web/assets/icon_downArrow.svg';
import { Checkbox } from '../../../../../../../web/components/Checkbox';
import { Input } from '../../../../../../../web/components-v2/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../../../web/components-v2/Popover';
import { ISelectBoxOption } from '../../../../../../../web/components/SelectBox';
import { bs } from '../../../../../../../web/styles/styles';
import { styleSheet } from './styles';

export const AdditionalSurveyQuestionInputField = ({
	field,
	index,
	filteredQuestions,
	value,
	handleFieldChange,
	onBlur,
}: {
	field: IFormField<string>;
	index: number;
	filteredQuestions: IFormField<string>[];
	value: string;
	handleFieldChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}) => {
	return (
		<div key={field.id}>
			<div className={css(styleSheet.inputWrap)}>
				<div className={css(styleSheet.inputWrapHeader)}>
					<label className={css(bs.block, bs.textLeft)} htmlFor={field.id}>
						{field.label} {!field?.isOptional ? <sup className={css(styleSheet.errorMessage)}>*</sup> : null}
					</label>{' '}
					<small className={css(styleSheet.smallText, bs.shrink0)}>
						{`${index + 1} of ${filteredQuestions.length}`}
					</small>
				</div>
				<Input
					type='text'
					id={field.id}
					placeholder='Feedback'
					value={value || ''}
					styles={[styleSheet.textInput]}
					onBlur={onBlur}
					onChange={handleFieldChange}
				/>
			</div>
		</div>
	);
};

export const AdditionalSurveyQuestionDropdownField = ({
	field,
	filteredQuestions,
	index,
	value,
	setValue,
}: {
	field: IFormField<string>;
	filteredQuestions: IFormField<string>[];
	index: number;
	value: string;
	setValue: (value: string) => void;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const fieldDropdownOptions: ISelectBoxOption<string>[] =
		field.options?.map(o => ({
			id: o.id,
			title: o.label,
			value: o.label,
		})) || [];

	const onSelectionChanged = (selection: ISelectBoxOption<string>) => {
		setValue(selection.id);
	};

	const selectedOption = fieldDropdownOptions.find(x => value === x.id);

	return (
		<div key={field.id} className={css(styleSheet.inputWrap)}>
			<div className={css(styleSheet.inputWrapHeader)}>
				<label className={css(bs.block, bs.textLeft)} htmlFor={field.id}>
					{field.label} {!field?.isOptional ? <sup className={css(styleSheet.errorMessage)}>*</sup> : null}
				</label>{' '}
				<small className={css(styleSheet.smallText, bs.shrink0)}>{`${index + 1} of ${filteredQuestions.length}`}</small>
			</div>
			<Popover open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
				<PopoverTrigger asChild>
					<button
						type='button'
						className={css(
							styleSheet.selectBoxtriggerButton,
							isOpen ? styleSheet.selectBoxtriggerButtonIsOpen : undefined
						)}
						onClick={() => setIsOpen(!isOpen)}
					>
						<span>{selectedOption ? selectedOption.value : 'Select option'}</span>
						<img src={DisclosureDownIconUrl} alt='Disclosure down icon' />
					</button>
				</PopoverTrigger>
				<PopoverContent side='bottom'>
					<div className={css(styleSheet.dropdownContent)}>
						{fieldDropdownOptions.map(x => (
							<button
								type='button'
								className={css(styleSheet.dropdownButton)}
								onClick={() => {
									onSelectionChanged(x);
									setIsOpen(false);
								}}
								key={x.id}
							>
								{x.title as React.ReactNode}
							</button>
						))}
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const AdditionalSurveyQuestionCheckboxField = ({
	field,
	filteredQuestions,
	index,
	value,
	setValue,
}: {
	field: IFormField<string>;
	filteredQuestions: IFormField<string>[];
	index: number;
	value: string;
	setValue: (value: string) => void;
}) => {
	// Split current value into an array of selected option ids.
	const fieldCheckboxSelectedOptions: string[] = value?.split(',')?.filter(Boolean) || [];

	const onChange = (e: React.ChangeEvent<HTMLInputElement>, optionId: string) => {
		let newOptions = [...fieldCheckboxSelectedOptions];
		if (e.target.checked) {
			newOptions.push(optionId);
		} else {
			newOptions = newOptions.filter(id => id !== optionId);
		}
		setValue(newOptions.join(','));
	};

	const fieldCheckboxOptions = field.options?.map(o => (
		<Checkbox
			key={o.id}
			id={o.id}
			className={css(styleSheet.checkbox)}
			checked={fieldCheckboxSelectedOptions.includes(o.id)}
			onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e, o.id)}
		>
			<span>{o.label}</span>
		</Checkbox>
	));

	return (
		<div key={field.id} className={css(styleSheet.inputWrap)}>
			<div className={css(styleSheet.inputWrapHeader)}>
				<label className={css(bs.block, bs.textLeft)} htmlFor={field.id}>
					{field.label} {!field?.isOptional ? <sup className={css(styleSheet.errorMessage)}>*</sup> : null}
				</label>{' '}
				<small className={css(styleSheet.smallText, bs.shrink0)}>{`${index + 1} of ${filteredQuestions.length}`}</small>
			</div>
			{fieldCheckboxOptions}
		</div>
	);
};
